import { tabNames } from '@va/constants';
import { getActiveTab } from '@va/dashboard/selectors/ui';
import { PageContent, PageHeader, TitleAndContextMenu } from '@va/dashboard/shared/ui-layout';
import { useDataView } from '@va/dashboard/util-hooks';
import { useTranslate } from '@va/localization';
import { DataViewOption } from '@va/types/report-block';
import { NestedNavigationBackButton, ReportBlock } from '@va/ui/design-system';
import { useRefreshContext } from '@va/util/components';
import { useCallback, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { VisitorHistoryContextProvider } from './VisitorHistoryContext';
import VisitorsHistoryGridView from './VisitorsHistoryGridView';
import VisitorsHistoryTable from './VisitorsHistoryTable';

export const VisitorsHistoryTab = () => {
  const translate = useTranslate();
  const history = useHistory();
  const activeTab = useSelector(getActiveTab) ?? tabNames.overview;

  const { manualRefresh } = useRefreshContext();

  const visitorsHistoryRef = useRef<{ refreshVisitorsHistoryData: () => void }>(null);
  const handleBackButtonClick = useCallback(() => {
    // new tab + current tab
    if (history.length > 2) {
      history.goBack();
      return;
    }
    history.push('/');
  }, [history]);

  return (
    <VisitorHistoryContextProvider ref={visitorsHistoryRef}>
      <PageHeader
        title={
          <TitleAndContextMenu
            title={
              <NestedNavigationBackButton
                onBackClick={handleBackButtonClick}
                parentPage={translate(`tab.${activeTab}`)}
                currentPage={translate('tab.visitorsHistory')}
              />
            }
            tooltipTransKey={'tooltipTab.visitorsHistory'}
            onBackClick={handleBackButtonClick}
          />
        }
        hideDatePicker
        refreshFn={() => {
          manualRefresh();
          visitorsHistoryRef?.current?.refreshVisitorsHistoryData();
        }}
      />
      <PageContent>
        <VisitorsHistoryPanel />
      </PageContent>
    </VisitorHistoryContextProvider>
  );
};

const VisitorsHistoryPanel = () => {
  const translate = useTranslate();
  const view = useDataView({
    defaultView: DataViewOption.table,
    identifier: 'visitors-history-content-view', // TODO Replace with report block id
    options: [DataViewOption.table, DataViewOption.card],
  });

  const viewComponents = useMemo(
    () => ({ [DataViewOption.table]: VisitorsHistoryTable, [DataViewOption.card]: VisitorsHistoryGridView }),
    [],
  );

  return (
    <ReportBlock
      title={translate('tab.visitorsHistory')}
      className='min-h-350'
      visualization={view}
      visualizationComponents={viewComponents}
    ></ReportBlock>
  );
};
